import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
    DataGrid,
    GridColDef,
    GridRenderCellParams
} from "@mui/x-data-grid";
import {
    Box,
    Button,
    TextField
} from "@mui/material";
import { GridSortModel, GridFilterModel } from "@mui/x-data-grid";


import Layout from "../components/Layout";
import axiosExt from "../axios";
import { API_BASE_URL } from "../config";
import { useTranslation } from "react-i18next";
import { useSnackbar } from 'notistack';
import { Service, ServicePrice } from '../types';

interface RowModel {
    id: string; // serviceId
    name: string;
    accountingNo?: string;
    price?: number;
    pricePerUnitAfter1stUnit?: number;
}

const CustomerPricingPage: React.FC = () => {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const customerId = searchParams.get("customerId");

    const [rows, setRows] = useState<RowModel[]>([]);
    const [loading, setLoading] = useState(false);

    const [dirtyRows, setDirtyRows] = useState<
        Record<string, { price: number; pricePerUnitAfter1stUnit: number }>
    >({});

    const { enqueueSnackbar } = useSnackbar();

    const [sortModel, setSortModel] = useState<GridSortModel>([]);
    const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });

    const savedSortKey = "sortCustomerPricingList";
    const savedFilterKey = "filterCustomerPricingList";
    // ---------------------------
    // FETCH DATA
    // ---------------------------
    useEffect(() => {

        if (!customerId) return;
        fetchData();
    }, [customerId]);

    useEffect(() => {
        const savedSortModel = JSON.parse(localStorage.getItem(savedSortKey) ?? "[]");
        const savedFilterModel = (() => {
            try {
                const parsed = JSON.parse(localStorage.getItem(savedFilterKey) ?? "{}");
                return parsed && parsed.items ? parsed : { items: [] };
            } catch (error) {
                console.error("Error parsing savedFilterKey:", error);
                return { items: [] }; // Fallback
            }
        })();
        setSortModel(savedSortModel);
        setFilterModel(savedFilterModel);
    }, []);

    // Save sortModel changes to localStorage
    const handleSortModelChange = (newModel: GridSortModel) => {
        setSortModel(newModel);
        localStorage.setItem(savedSortKey, JSON.stringify(newModel));
    };

    // Save filterModel changes to localStorage
    const handleFilterModelChange = (newModel: GridFilterModel) => {
        setFilterModel(newModel);
        localStorage.setItem(savedFilterKey, JSON.stringify(newModel));
    };

    const fetchData = async () => {
        setLoading(true);

        try {
            const token = localStorage.getItem("token");

            const [servicesRes, pricesRes] = await Promise.all([
                axiosExt.get<Service[]>(`${API_BASE_URL}/services`, {
                    headers: { Authorization: `Bearer ${token}` }
                }),
                axiosExt.get<ServicePrice[]>(
                    `${API_BASE_URL}/service-prices/by-customer/${customerId}`,
                    {
                        headers: { Authorization: `Bearer ${token}` }
                    }
                )
            ]);

            const services = servicesRes.data;
            const prices = pricesRes.data;

            const priceMap = new Map<string, ServicePrice>();
            prices.forEach(p => {
                priceMap.set(p.serviceId, p);
            });

            const merged: RowModel[] = services.map(s => {
                const sp = priceMap.get(s.id!);

                return {
                    id: s.id!,
                    name: s.name,
                    accountingNo: s.accountingNo ?? undefined,
                    price: sp?.price,
                    pricePerUnitAfter1stUnit: sp?.pricePerUnitAfter1stUnit ?? 0
                };
            });

            setRows(merged);
        } catch (err) {
            alert("Error loading pricing");
            console.error("Error loading pricing:", err);
        } finally {
            setLoading(false);
        }
    };

    // ---------------------------
    // HANDLE EDIT
    // ---------------------------
    const handlePriceChange = (serviceId: string, value: string) => {
        const numeric = parseFloat(value);

        setRows(prev =>
            prev.map(r =>
                r.id === serviceId
                    ? { ...r, price: isNaN(numeric) ? undefined : numeric }
                    : r
            )
        );

        const currentRow = rows.find(r => r.id === serviceId);

        setDirtyRows(prev => ({
            ...prev,
            [serviceId]: {
                price: isNaN(numeric) ? 0 : numeric,
                pricePerUnitAfter1stUnit:
                    prev[serviceId]?.pricePerUnitAfter1stUnit ??
                    currentRow?.pricePerUnitAfter1stUnit ??
                    0
            }
        }));
    };

    //const handlePriceAfterChange = (serviceId: string, value: string) => {
    //    const numeric = parseFloat(value);

    //    setRows(prev =>
    //        prev.map(r =>
    //            r.id === serviceId
    //                ? { ...r, pricePerUnitAfter1stUnit: isNaN(numeric) ? 0 : numeric }
    //                : r
    //        )
    //    );

    //    const currentRow = rows.find(r => r.id === serviceId);

    //    setDirtyRows(prev => ({
    //        ...prev,
    //        [serviceId]: {
    //            price:
    //                prev[serviceId]?.price ??
    //                currentRow?.price ??
    //                0,
    //            pricePerUnitAfter1stUnit: isNaN(numeric) ? 0 : numeric
    //        }
    //    }));
    //};

    // ---------------------------
    // SAVE (BULK)
    // ---------------------------
    const handleSave = async () => {
        if (!customerId) return;

        try {
            const token = localStorage.getItem("token");

            const payload = Object.entries(dirtyRows).map(
                ([serviceId, values]) => ({
                    serviceId,
                    customerId,
                    price: values.price,
                    pricePerUnitAfter1stUnit: values.pricePerUnitAfter1stUnit
                })
            );

            await axiosExt.post(
                `${API_BASE_URL}/service-prices/bulk-upsert`,
                payload,
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );
            enqueueSnackbar(`Pricing saved`,
                {
                    variant: "success",
                    autoHideDuration: 5000,
                });
            setDirtyRows({});
            fetchData();
        } catch (err) {
            enqueueSnackbar(`Error saving prices`,
                {
                    variant: "error",
                    autoHideDuration: 5000,
                });
            console.error("Error saving prices:", err);
        }
    };

    // ---------------------------
    // COLUMNS
    // ---------------------------
    const columns: GridColDef[] = [
        { field: "name", headerName: t("Service"), width: 200 },
        { field: "accountingNo", headerName: t("Accounting"), width: 150 },
        {
            field: "price",
            headerName: t("Price"),
            width: 150,
            renderCell: (params: GridRenderCellParams) => (
                <TextField
                    size="small"
                    type="number"
                    value={params.row.price ?? ""}
                    onChange={(e) =>
                        handlePriceChange(params.row.id, e.target.value)
                    }
                />
            )
        },
        //{
        //    field: "pricePerUnitAfter1stUnit",
        //    headerName: t("Price After 1st Unit"),
        //    width: 200,
        //    renderCell: (params: GridRenderCellParams) => (
        //        <TextField
        //            size="small"
        //            type="number"
        //            value={params.row.pricePerUnitAfter1stUnit ?? 0}
        //            onChange={(e) =>
        //                handlePriceAfterChange(params.row.id, e.target.value)
        //            }
        //        />
        //    )
        //}
    ];

    // ---------------------------
    // RENDER
    // ---------------------------
    return (
        <Layout>
            <Box sx={{ width: "100%", display: "flex", flexDirection: "column" }}>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "left",
                        alignItems: "center",
                        mb: 2,
                        gap:2
                    }}
                >
                    <h2>{t("Customer Pricing")}</h2>

                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSave}
                        disabled={Object.keys(dirtyRows).length === 0}
                    >
                        {t("Save")}
                    </Button>
                </Box>

                <Box
                    sx={{
                        flexGrow: 1,
                        width: "100%",
                        height: "calc(100vh - 220px)"
                    }}
                >
                    <DataGrid
                        rows={rows}
                        columns={columns}
                        sortModel={sortModel}
                        onSortModelChange={handleSortModelChange}
                        filterModel={filterModel}
                        onFilterModelChange={handleFilterModelChange}
                        getRowId={(r) => r.id}
                        loading={loading}
                        pageSizeOptions={[25, 50, 100]}
                        initialState={{
                            pagination: {
                                paginationModel: { pageSize: 25, page: 0 }
                            }
                        }}
                    />
                </Box>
            </Box>
        </Layout>
    );
};

export default CustomerPricingPage;